/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import BookmarkButton from '@/components/BookmarkButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, Plus, ExternalLink, Pill, Stethoscope, FileText, Smartphone, ThumbsUp, Search, Trash2, HeartHandshake, Flag, Globe, Apple } from 'lucide-react';
import { EmptyState } from '@/components/UIExtras';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useAuth } from '@/lib/auth';

const TYPE_META = {
  pharmacology: { icon: Pill, color: 'var(--wl-primary)' },
  procedure: { icon: Stethoscope, color: '#1D4ED8' },
  guideline: { icon: FileText, color: '#7C3AED' },
  app: { icon: Smartphone, color: '#B45309' },
  wellness: { icon: HeartHandshake, color: '#DB2777' },
  protocol_it: { icon: Flag, color: '#0EA5E9' },
  national_app: { icon: Globe, color: 'var(--wl-primary-alt)' },
  other: { icon: BookOpen, color: '#475569' },
};

const CreateResourceDialog = ({ onCreated }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', url: '', type: 'pharmacology', category: '' });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.title.trim() || !form.url.trim()) return;
    setSubmitting(true);
    try {
      const payload = { ...form, category: form.category || null, url: form.url.trim() };
      if (!/^https?:\/\//.test(payload.url)) payload.url = 'https://' + payload.url;
      const res = await api.post('/resources', payload);
      onCreated && onCreated(res.data);
      toast.success(t('resourceCreated'));
      setOpen(false);
      setForm({ title: '', description: '', url: '', type: 'pharmacology', category: '' });
    } catch { toast.error(t('errorOccurred')); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="resource-create-open-button">
          <Plus className="h-4 w-4" />{t('createResource')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[520px] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t('createResource')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">{t('type')}</Label>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-1.5">
              {Object.entries(TYPE_META).map(([k, meta]) => {
                const Icon = meta.icon; const active = form.type === k;
                return (
                  <button key={k} type="button" onClick={() => set('type', k)} className={`p-2 rounded-xl border text-[11px] flex flex-col items-center gap-1 ${active ? 'ring-2 ring-primary/50 border-primary/50 bg-primary/5' : 'border-border hover:bg-secondary/50'}`} data-testid={`resource-type-${k}`}>
                    <Icon className="h-4 w-4" style={{ color: meta.color }} />
                    {t(k)}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('title')}</Label>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} data-testid="resource-title-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('link')}</Label>
            <Input value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://..." data-testid="resource-url-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('description')}</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="resource-description-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('category')}</Label>
            <div className="mt-2 flex gap-1 flex-wrap">
              {[['', t('all')], ['medici', t('medici')], ['infermieri', t('infermieri')], ['oss', t('oss')]].map(([k, label]) => (
                <button key={k || 'all'} type="button" onClick={() => set('category', k)} className={`text-[11px] px-2 py-1 rounded-full border ${form.category === k ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border text-muted-foreground'}`}>{label}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button onClick={submit} disabled={submitting || !form.title.trim() || !form.url.trim()} data-testid="resource-create-submit-button">
            {submitting ? t('loading') : t('publish')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ResourceCard = ({ item, onChanged, onDeleted }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const meta = TYPE_META[item.type] || TYPE_META.other;
  const Icon = meta.icon;
  const [upv, setUpv] = useState(false);
  const isOwner = user?.id === item.author?.id;
  const canDelete = isOwner || user?.is_admin;

  const toggle = async () => {
    if (upv) return;
    setUpv(true);
    try {
      const res = await api.post(`/resources/${item.id}/upvote`);
      onChanged && onChanged({ ...item, upvoted_by_me: res.data.upvoted, upvote_count: res.data.upvote_count });
    } catch { toast.error(t('errorOccurred')); }
    finally { setUpv(false); }
  };

  const [deleting, setDeleting] = useState(false);
  const del = async () => {
    if (deleting || !window.confirm(t('confirmDelete'))) return;
    setDeleting(true);
    try { await api.delete(`/resources/${item.id}`); onDeleted && onDeleted(item.id); toast.success(t('delete')); }
    catch { toast.error(t('errorOccurred')); }
    finally { setDeleting(false); }
  };

  return (
    <Card className="p-4 rounded-2xl bg-card border border-border" data-testid="resource-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${meta.color}18`, color: meta.color }}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold uppercase" style={{ color: meta.color }}>{t(item.type)}</span>
            {item.category && <span className="text-[11px] text-muted-foreground">· {t(item.category)}</span>}
          </div>
          <a href={item.url} target="_blank" rel="noopener noreferrer" className="font-display font-semibold text-base leading-tight hover:text-primary block truncate" data-testid="resource-title-link">{item.title}</a>
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{item.url}</div>
          {item.description && <p className="text-sm mt-2 whitespace-pre-wrap line-clamp-3">{item.description}</p>}
          {(item.ios_url || item.android_url) && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="resource-store-links">
              {item.ios_url && (
                <a href={item.ios_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-secondary hover:bg-secondary/70 text-foreground transition-colors"
                   data-testid="resource-ios-link">
                  <Apple className="h-3.5 w-3.5" />
                  App Store
                </a>
              )}
              {item.android_url && (
                <a href={item.android_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-secondary hover:bg-secondary/70 text-foreground transition-colors"
                   data-testid="resource-android-link">
                  <Smartphone className="h-3.5 w-3.5" />
                  Google Play
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="text-[11px] text-muted-foreground">{item.author?.full_name}</div>
        <div className="flex items-center gap-1">
          {canDelete && (<Button variant="ghost" size="sm" onClick={del} disabled={deleting} aria-label={t('delete')} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>)}
          <BookmarkButton targetType="resource" targetId={item.id} size="sm" />
          <Button variant="ghost" size="sm" onClick={toggle} disabled={upv} className={item.upvoted_by_me ? 'text-primary' : ''} data-testid="resource-upvote-button">
            <ThumbsUp className={`h-4 w-4 mr-1 ${item.upvoted_by_me ? 'fill-primary' : ''}`} /> {item.upvote_count || 0}
          </Button>
          <Button asChild size="sm" data-testid="resource-open-button">
            <a href={item.url} target="_blank" rel="noopener noreferrer">{t('open')} <ExternalLink className="h-3.5 w-3.5 ml-1" /></a>
          </Button>
        </div>
      </div>
    </Card>
  );
};

const ResourcesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPatient = user?.role === 'patient';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.type = typeFilter;
      if (q.trim()) params.q = q.trim();
      const res = await api.get('/resources', { params });
      setItems(res.data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [typeFilter, q]);

  // Patients only see a subset of tabs (institutional apps + monitoring)
  const patientTypeEntries = Object.entries(TYPE_META).filter(([k]) =>
    ['app', 'national_app', 'pharmacology'].includes(k)
  );
  const typeEntries = isPatient ? patientTypeEntries : Object.entries(TYPE_META);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="resources-title">
            {isPatient ? t('patientHealthAppsNav') : t('resources')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isPatient ? t('patientHealthAppsSubtitle') : t('resourcesDesc')}
          </p>
        </div>
        {!isPatient && <CreateResourceDialog onCreated={(x) => setItems(prev => [x, ...prev])} />}
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('search')} className="pl-9" data-testid="resources-search-input" />
      </div>
      <Tabs value={typeFilter} onValueChange={setTypeFilter}>
        <TabsList className="w-full flex flex-wrap justify-start gap-1 bg-secondary/60 p-1 rounded-full h-auto">
          <TabsTrigger value="all" className="rounded-full text-xs" data-testid="resources-tab-all">{t('all')}</TabsTrigger>
          {typeEntries.map(([k, meta]) => {
            const Icon = meta.icon;
            return (
              <TabsTrigger key={k} value={k} className="rounded-full text-xs gap-1" data-testid={`resources-tab-${k}`}>
                <Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
                {t(k)}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {loading && <div className="text-sm text-muted-foreground">{t('loading')}</div>}
        {!loading && items.length === 0 && (
          <div className="md:col-span-2"><EmptyState icon={<BookOpen className="h-6 w-6" />} title={t('noResources')} subtitle={isPatient ? t('patientHealthAppsSubtitle') : t('resourcesDesc')} /></div>
        )}
        {items.map(i => (
          <motion.div key={i.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
            <ResourceCard item={i} onChanged={(u) => setItems(prev => prev.map(x => x.id === u.id ? u : x))} onDeleted={(id) => setItems(prev => prev.filter(x => x.id !== id))} />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ResourcesPage;
